// src/pages/Dashboard/MarketingDashboard.js
// Dashboard for MarketingManager (team view) and MarketingExecutive (own view).
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, Flame, Sun, Snowflake, Users, Store, Truck, UserCheck,
  CalendarCheck, Percent, Plus, Navigation, Calendar,
} from 'lucide-react';
import { StatCard, SectionHeader } from '../../components/ui/UI';
import { selectAuthState } from '../../store/selectors/authSelector';
import {
  fetchExecutiveSummary,
  fetchMyReports,
  fetchAllReports,
} from '../../services/features/visitReport/visitReportSlice';
import { fetchMonthlyAttendance } from '../../services/features/attendance/attendanceSlice';
import {
  computeRange,
  formatDateKey,
  formatDateTime,
  monthLabel,
  ordinal,
  FLOW_META,
} from '../../utils/attendanceFormat';
import '../Shared/ReportPages.css';

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const ROLE_LABEL = { MarketingManager: 'Marketing Manager', MarketingExecutive: 'Marketing Executive' };

const MarketingDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, role } = useSelector(selectAuthState);
  const isManager = role === 'MarketingManager';

  const { execSummary, myList, allList } = useSelector((s) => s.visitReport);
  const { monthly } = useSelector((s) => s.attendance);
  const retailers = useSelector((s) => s.retailer?.list) || [];
  const distributors = useSelector((s) => s.distributors?.list) || [];
  const fseList = useSelector((s) => s.fse?.list) || [];

  const [range, setRange] = useState('month');
  const { from, to } = useMemo(() => computeRange(range), [range]);
  const rangeLabel = PRESETS.find((p) => p.key === range)?.label;
  const month = formatDateKey().slice(0, 7);

  useEffect(() => {
    const p = { from: from || undefined, to: to || undefined };
    dispatch(fetchExecutiveSummary(p));
    dispatch(isManager ? fetchAllReports({ ...p, limit: 8 }) : fetchMyReports({ ...p, limit: 8 }));
  }, [dispatch, from, to, isManager]);

  useEffect(() => {
    dispatch(fetchMonthlyAttendance({ month }));
  }, [dispatch, month]);

  const totals = execSummary?.totals || { reports: 0, hot: 0, warm: 0, cold: 0, active: 0, people: 0 };
  const people = execSummary?.people || [];
  const customers = people.reduce((n, p) => n + (p.uniqueCustomers || 0), 0);
  const recent = (isManager ? allList : myList).slice(0, 8);

  const att = monthly?.month === month ? monthly : null;
  const sum = att?.summary || {};

  const qs = new URLSearchParams({ ...(from && { from }), ...(to && { to }), label: rangeLabel || '' }).toString();

  return (
    <div className="rp-page">
      {/* Welcome + range */}
      <div className="rp-toolbar">
        <div>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: 22, fontWeight: 800 }}>
            Welcome, {user?.name || 'there'}
          </div>
          <div className="rp-sub" style={{ fontSize: 13 }}>
            {ROLE_LABEL[role]}
            {user?.district ? ` · ${user.district}` : ''}
          </div>
        </div>
        <div className="rp-spacer" />
        <div className="rp-chips">
          {PRESETS.map((p) => (
            <button key={p.key} className={`rp-chip ${range === p.key ? 'active' : ''}`} onClick={() => setRange(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="rp-chips">
        <button className="rp-chip active" onClick={() => navigate('/visit-reports/new')}>
          <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> New Visit Report
        </button>
        <button className="rp-chip" onClick={() => navigate(isManager ? '/visit-reports' : '/visit-reports/list')}>
          <ClipboardList size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Visit Reports
        </button>
        <button className="rp-chip" onClick={() => navigate(isManager ? '/attendance' : '/attendance/me')}>
          <Calendar size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> {isManager ? 'Attendance' : 'My Attendance'}
        </button>
        <button className="rp-chip" onClick={() => navigate('/fse-tracking')}>
          <Navigation size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> FSE Tracking
        </button>
      </div>

      {/* Visit stats */}
      <SectionHeader title={isManager ? `Team Visit Reports · ${rangeLabel}` : `My Visit Reports · ${rangeLabel}`} />
      <div className="stats-grid">
        <StatCard icon={<ClipboardList size={20} />} label="Total Visits" value={totals.reports} accent="red"
          onClick={() => navigate(`/visit-reports/list?${qs}`)} />
        <StatCard icon={<Flame size={20} />} label="Hot Leads" value={totals.hot} accent="red" />
        <StatCard icon={<Sun size={20} />} label="Warm Leads" value={totals.warm} accent="yellow" />
        <StatCard icon={<Snowflake size={20} />} label="Cold Leads" value={totals.cold} accent="blue" />
        <StatCard icon={<Users size={20} />} label="Customers" value={customers} accent="teal" />
        {isManager && (
          <StatCard icon={<UserCheck size={20} />} label="Submitted / Team" value={`${totals.active}/${totals.people}`} accent="green"
            onClick={() => navigate('/visit-reports')} />
        )}
      </div>

      {/* My attendance */}
      <SectionHeader title={`My Attendance · ${monthLabel(month)}`} />
      <div className="stats-grid">
        <StatCard icon={<CalendarCheck size={20} />} label="Full Day" value={sum.present || 0} accent="green" />
        <StatCard icon={<CalendarCheck size={20} />} label="Half Day" value={sum.halfDay || 0} accent="blue" />
        <StatCard icon={<CalendarCheck size={20} />} label="Absent" value={sum.absent || 0} accent="red" />
        <StatCard icon={<Percent size={20} />} label="Attendance" value={`${Number(att?.attendancePct || 0).toFixed(1)}%`} accent="yellow"
          onClick={() => navigate(isManager ? `/attendance/${user?._id}?name=${encodeURIComponent(user?.name || '')}&role=${role}` : '/attendance/me')} />
      </div>

      {/* Network */}
      <SectionHeader title="My Network" />
      <div className="stats-grid">
        <StatCard icon={<Store size={20} />} label="Retailers" value={retailers.length} accent="green" onClick={() => navigate('/retailers')} />
        <StatCard icon={<Truck size={20} />} label="Distributors" value={distributors.length} accent="blue" onClick={() => navigate('/distributors')} />
        <StatCard icon={<Users size={20} />} label="Field Execs" value={fseList.length} accent="yellow" onClick={() => navigate('/fse')} />
      </div>

      {/* Team table (manager) */}
      {isManager && people.length > 0 && (
        <>
          <SectionHeader title="My Team" count={people.length} />
          <div className="rp-card">
            <div className="rp-table-wrap">
              <table className="rp-table">
                <thead>
                  <tr><th>Person</th><th className="rp-num">Reports</th><th>Lead split</th><th>Last report</th></tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={String(p.executiveId)} className="clickable"
                      onClick={() => navigate(`/visit-reports/list?executiveId=${p.executiveId}&name=${encodeURIComponent(p.name || '')}&${qs}`)}>
                      <td>
                        <div className="rp-person">
                          <div className="rp-avatar">{(p.name || '?').charAt(0).toUpperCase()}</div>
                          <div>
                            <div className="rp-strong">{p.name}</div>
                            <div className="rp-sub">{ROLE_LABEL[p.role] || p.role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="rp-num rp-strong">{p.total}</td>
                      <td>
                        <div className="rp-pills">
                          {['HOT', 'WARM', 'COLD'].map((f) => (
                            <span key={f} className="rp-pill" style={{ background: FLOW_META[f].bg, color: FLOW_META[f].color }}>
                              {FLOW_META[f].label} {p[f.toLowerCase()]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={p.total ? '' : 'rp-muted'}>{p.total ? formatDateTime(p.lastSubmittedAt) : 'No reports'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Recent reports */}
      <SectionHeader title="Recent Visit Reports" />
      <div className="rp-card">
        {recent.length === 0 ? (
          <div className="rp-empty">
            <b>No visit reports for {rangeLabel?.toLowerCase()}</b>
            <button className="rp-chip active" style={{ marginTop: 12 }} onClick={() => navigate('/visit-reports/new')}>
              Submit a visit report
            </button>
          </div>
        ) : (
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr><th>Customer</th><th>Mobile</th><th>Lead</th><th>Visit</th>{isManager && <th>By</th>}<th>Date</th></tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const m = FLOW_META[r.statusFlow] || FLOW_META.WARM;
                  return (
                    <tr key={r._id} className="clickable" onClick={() => navigate('/visit-reports/list')}>
                      <td>
                        <div className="rp-strong">{r.name}</div>
                        <div className="rp-sub">{r.businessName}</div>
                      </td>
                      <td>{r.mobile}</td>
                      <td><span className="rp-pill" style={{ background: m.bg, color: m.color }}>{m.label.toUpperCase()}</span></td>
                      <td>
                        {r.isRevisit
                          ? <span className="rp-pill rp-revisit">REVISIT · {ordinal(r.visitNumber)}</span>
                          : <span className="rp-muted">1st visit</span>}
                      </td>
                      {isManager && <td>{r.executiveName || r.executiveId?.name || '—'}</td>}
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.submittedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingDashboard;
